import { useEffect, useState } from "react";
import { X, Cookie } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context";
import { T } from "@/i18n";
import { LeadForm } from "@/components/LeadForm";

export function ScrollToTop() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return null;
}

export function CookieBanner() {
  const { lang } = useLang();
  const t = T[lang].cookie;
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!localStorage.getItem("iim_consent")) setVisible(true);
  }, []);

  const save = (value) => {
    localStorage.setItem("iim_consent", JSON.stringify({ necessary: true, ...value, date: new Date().toISOString() }));
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-4" data-testid="cookie-banner">
      <div className="mx-auto max-w-3xl rounded-2xl bg-[#1F2328] text-[#F6EFE4] shadow-2xl p-6">
        <div className="flex items-start gap-3">
          <Cookie className="text-[#E8B64C] shrink-0 mt-0.5" size={22} />
          <div>
            <p className="font-serif text-lg">{t.title}</p>
            <p className="text-sm text-[#F6EFE4]/70 mt-1 leading-relaxed">{t.text}</p>
          </div>
        </div>
        {customize && (
          <div className="mt-4 space-y-2 text-sm border-t border-[#F6EFE4]/10 pt-4">
            <label className="flex items-center gap-3"><input type="checkbox" checked disabled className="h-4 w-4 accent-[#E8B64C]" /> {t.necessary}</label>
            <label className="flex items-center gap-3"><input type="checkbox" checked={prefs.analytics} onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))} className="h-4 w-4 accent-[#E8B64C]" data-testid="cookie-analytics-toggle" /> {t.analytics}</label>
            <label className="flex items-center gap-3"><input type="checkbox" checked={prefs.marketing} onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))} className="h-4 w-4 accent-[#E8B64C]" data-testid="cookie-marketing-toggle" /> {t.marketing}</label>
          </div>
        )}
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={() => save({ analytics: true, marketing: true })} className="rounded-full bg-[#B5472B] px-6 py-2.5 text-sm font-medium hover:bg-[#93391f] transition-colors" data-testid="cookie-accept">{t.accept}</button>
          <button onClick={() => save({ analytics: false, marketing: false })} className="rounded-full bg-transparent border border-[#F6EFE4]/40 px-6 py-2.5 text-sm font-medium hover:bg-[#F6EFE4]/10 transition-colors" data-testid="cookie-reject">{t.reject}</button>
          {customize ? (
            <button onClick={() => save(prefs)} className="rounded-full bg-transparent border border-[#F6EFE4]/40 px-6 py-2.5 text-sm font-medium hover:bg-[#F6EFE4]/10 transition-colors" data-testid="cookie-save">{t.save}</button>
          ) : (
            <button onClick={() => setCustomize(true)} className="rounded-full bg-transparent border border-[#F6EFE4]/40 px-6 py-2.5 text-sm font-medium hover:bg-[#F6EFE4]/10 transition-colors" data-testid="cookie-customize">{t.customize}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function GuidePopup() {
  const { lang } = useLang();
  const t = T[lang].popup;
  const [show, setShow] = useState(false);

  useEffect(() => {
    const last = localStorage.getItem("iim_guide_popup");
    if (last && Date.now() - Number(last) < 14 * 24 * 3600 * 1000) return;
    const trigger = () => {
      setShow(true);
      localStorage.setItem("iim_guide_popup", String(Date.now()));
    };
    const timer = setTimeout(trigger, 45000);
    const onScroll = () => {
      const pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight || 1);
      if (pct >= 0.5) {
        trigger();
        window.removeEventListener("scroll", onScroll);
        clearTimeout(timer);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => { clearTimeout(timer); window.removeEventListener("scroll", onScroll); };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#1F2328]/60 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="guide-popup"
          onClick={() => setShow(false)}>
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-3xl bg-[#F6EFE4] p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShow(false)} aria-label={t.close || "Close"} data-testid="guide-popup-close"
              className="absolute top-4 right-4 h-9 w-9 rounded-full bg-[#0E4D5C]/10 flex items-center justify-center text-[#0E4D5C] hover:bg-[#0E4D5C]/20 transition-colors">
              <X size={18} />
            </button>
            <p className="eyebrow">{lang === "it" ? "Guida gratuita" : "Free guide"}</p>
            <h3 className="font-serif text-2xl text-[#0E4D5C] mt-2">{t.title}</h3>
            <p className="text-sm text-[#1F2328]/70 mt-3 leading-relaxed">{t.text}</p>
            <div className="mt-5">
              <LeadForm type="guide" compact testIdPrefix="popup-guide-form" />
            </div>
            <button onClick={() => setShow(false)} className="mt-3 w-full text-center text-xs text-[#8A8F96] hover:text-[#1F2328]" data-testid="guide-popup-later">{t.later}</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
